import React, { useState, useEffect, useRef } from "react";
import {
  Segment,
  Header,
  Loader,
  Message,
  List,
  Label,
} from "semantic-ui-react";
import { circulationStatsApi } from "@api/stats";
import { sessionManager } from "@authentication/services/SessionManager";

const TopBorrowers = () => {
  const [topBorrowers, setTopBorrowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const currentUser = sessionManager.user;

  useEffect(() => {
    const fetchTopBorrowers = async () => {
      try {
        if (!isMountedRef.current) return;

        setLoading(true);
        setError(null);

        const response = await circulationStatsApi.getTopBorrowers(null, null);

        // Check if component is still mounted before updating state
        if (!isMountedRef.current) return;

        const allBorrowers = (response.data?.hits || []).sort(
          (a, b) => (b.loan_count || 0) - (a.loan_count || 0)
        );

        const topTen = allBorrowers.slice(0, 10);

        if (currentUser && currentUser.id) {
          const userIndex = allBorrowers.findIndex(
            (b) => String(b.patron_pid) === String(currentUser.id)
          );

          if (userIndex !== -1) {
            const userRank = userIndex + 1;
            const isInTopTen = userIndex < 10;

            if (!isInTopTen) {
              topTen.push({
                ...allBorrowers[userIndex],
                _isCurrentUser: true,
                _userRank: userRank,
              });
            } else {
              topTen[userIndex] = {
                ...topTen[userIndex],
                _isCurrentUser: true,
                _userRank: userRank,
              };
            }
          }
        }

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setTopBorrowers(topTen);
          setLoading(false);
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setError("Failed to load top borrowers.");
          setLoading(false);
        }
      }
    };

    fetchTopBorrowers();

    // Cleanup function - mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [currentUser]);

  const truncate = (text, max = 25) =>
    text && text.length > max ? text.slice(0, max) + "..." : text;

  return (
    <Segment
      data-tour="top-borrowers"
      raised
      padded="very"
      style={{ width: "100%", maxHeight: "300vh", overflowY: "auto" }}
    >
      <Header as="h2" color="green" textAlign="center" dividing>
        Top Borrowers
      </Header>

      {loading ? (
        <Loader active inline="centered">
          Loading top borrowers...
        </Loader>
      ) : error ? (
        <Message negative content={error} />
      ) : topBorrowers.length === 0 ? (
        <Message info content="No borrowers found." />
      ) : (
        <List divided relaxed>
          {topBorrowers.map((borrower, idx) => {
            const isCurrentUser = borrower._isCurrentUser;
            const rankNumber = borrower._userRank || idx + 1;

            return (
              <List.Item
                key={borrower.id || idx}
                style={isCurrentUser ? { backgroundColor: "#e8f5e9" } : {}}
              >
                <List.Content>
                  <List.Header>
                    <Label color={isCurrentUser ? "teal" : "green"} circular>
                      #{rankNumber}
                    </Label>{" "}
                    {truncate(borrower.name || "N/A")}
                    {isCurrentUser && (
                      <Label
                        color="teal"
                        size="mini"
                        style={{ marginLeft: "0.5em" }}
                      >
                        You
                      </Label>
                    )}
                  </List.Header>
                  <List.Description>
                    Loans: {borrower.loan_count || 0}
                  </List.Description>
                </List.Content>
              </List.Item>
            );
          })}
        </List>
      )}
    </Segment>
  );
};

export default TopBorrowers;
